if (!customElements.get('vg-videos-slider')) {
  class VgVideosSlider extends HTMLElement {
    connectedCallback() {
      this.init();

      if (Shopify.designMode) {
        this.sectionLoadHandler = this.init.bind(this);
        document.addEventListener('shopify:section:load', this.sectionLoadHandler);
      }
    }

    disconnectedCallback() {
      if (this.swiper) this.swiper.destroy(true, true);
      if (this.observer) this.observer.disconnect();
      if (this.sectionLoadHandler) {
        document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
      }
    }

    init() {
      this.videos = Array.from(this.querySelectorAll('video'));
      this.videos.forEach((video) => {
        video.playsInline = true;
      });
      this.observePlayback();
      this.bindPlayButtons();

      /* The CSS row already lays the videos out correctly, so a missing Swiper is not fatal. */
      if (typeof Swiper === 'undefined') return;

      if (this.swiper) this.swiper.destroy(true, true);

      const container = this.querySelector('.swiper');
      if (!container) return;

      let options = {};
      try {
        options = JSON.parse(this.getAttribute('data-swiper-options')) || {};
      } catch (error) {
        options = {};
      }

      const perRowDesktop = parseFloat(options.slidesPerViewDesktop) || 3;
      const slideCount = this.querySelectorAll('.swiper-slide').length;

      /* Looping needs enough slides to fill the row twice, otherwise Swiper renders gaps. */
      const loop = options.loop === true && slideCount > perRowDesktop * 2;

      this.swiper = new Swiper(container, {
        slidesPerView: 'auto',
        spaceBetween: options.spaceBetween || 12,
        loop: loop,
        watchOverflow: true,
        navigation: {
          prevEl: options.prevEl,
          nextEl: options.nextEl,
        },
      });
    }

    /* Without autoplay, each video opens in the modal with sound and controls. */
    bindPlayButtons() {
      const modalId = this.dataset.modal;
      if (!modalId) return;

      this.querySelectorAll('[data-vg-play]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.openModal(button.closest('.vg-videos__slide').querySelector('video'), button);
        });
      });
    }

    openModal(video, opener) {
      /* ModalDialog moves itself to <body> on connect, so look it up from the document. */
      const modal = document.getElementById(this.dataset.modal);
      if (!modal || !video) return;

      const media = modal.querySelector('[data-vg-modal-media]');
      media.replaceChildren();

      const clone = video.cloneNode(true);
      clone.muted = false;
      clone.controls = true;
      clone.autoplay = true;
      clone.loop = this.dataset.videoLoop === 'true';
      clone.playsInline = true;
      media.appendChild(clone);

      modal.show(opener);
      clone.play().catch(() => {});

      /* Drop the clone once the modal closes, so it stops downloading and playing. */
      document.body.addEventListener(
        'modalClosed',
        () => {
          media.replaceChildren();
          /*
           * ModalDialog.hide calls unpauseAllMedia() right after dispatching this event,
           * and that plays every <video> on the page. Re-pause the ones that never
           * autoplayed, on the next tick so we run after it.
           */
          setTimeout(() => {
            this.videos.forEach((video) => {
              if (!video.autoplay) video.pause();
            });
          }, 0);
        },
        { once: true }
      );
    }

    /* Only autoplay while the block is on screen, so offscreen videos don't decode needlessly. */
    observePlayback() {
      const autoplaying = this.videos.filter((video) => video.autoplay);
      if (!autoplaying.length) return;

      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.play().catch(() => {});
            } else {
              entry.target.pause();
            }
          });
        },
        { threshold: 0.25 }
      );

      autoplaying.forEach((video) => this.observer.observe(video));
    }
  }

  customElements.define('vg-videos-slider', VgVideosSlider);
}
